import type { IProjectList } from "../types";

export interface IResolveInitialProjectUuidProps {
  projects: IProjectList;
  lastUsedProjectUuid: string | null;
  preferredOrganizationUuid?: string | null;
}

interface IGetFirstProjectUuidInOrganizationProps {
  projects: IProjectList;
  organizationUuid: string;
}

export interface IResolveActiveProjectUuidProps {
  projects: IProjectList;
  currentProjectUuid: string | null;
  lastUsedProjectUuid: string | null;
  preferredOrganizationUuid?: string | null;
}

export const getHasAnyProjects = (projects?: IProjectList | null): boolean => {
  if (!projects) {
    return false;
  }

  const hasPersonalProject = !!projects.personalProject;
  const hasOrganizationProjects = projects.organizationProjects.some(
    (organization) => organization.projects.length > 0,
  );

  return hasPersonalProject || hasOrganizationProjects;
};

// NOTE: True when the user belongs to at least one org that has a search
// embedding model configured. Used to surface cross-chat search on on-premise
// builds (where it is otherwise hidden) once an org wires up an embedding model.
export const getHasOrgWithSearchEmbeddingModel = (
  projects?: IProjectList | null,
): boolean => {
  if (!projects) {
    return false;
  }

  return projects.organizationProjects.some(
    (organization) => organization.hasSearchEmbeddingModel === true,
  );
};

const getAccessibleProjectUuids = (projects: IProjectList): Set<string> => {
  const accessibleProjectUuids = new Set<string>();

  for (const organization of projects.organizationProjects) {
    for (const project of organization.projects) {
      accessibleProjectUuids.add(project.uuid);
    }
  }

  if (projects.personalProject) {
    accessibleProjectUuids.add(projects.personalProject.uuid);
  }

  return accessibleProjectUuids;
};

// NOTE: Is this project still one the user can act in? The project list is the
// only authority — it is derived server-side from live (non-soft-deleted)
// memberships, so a project missing from it is a project whose membership is
// gone.
export const getIsProjectAccessible = ({
  projects,
  projectUuid,
}: {
  projects: IProjectList;
  projectUuid: string;
}): boolean => getAccessibleProjectUuids(projects).has(projectUuid);

// NOTE: Default landing — first org project (so org branding wins), else personal.
const getDefaultProjectUuid = (projects: IProjectList): string | null => {
  const firstOrganizationProject = projects.organizationProjects.find(
    (organization) => organization.projects.length > 0,
  )?.projects[0];

  if (firstOrganizationProject) {
    return firstOrganizationProject.uuid;
  }

  if (projects.personalProject) {
    return projects.personalProject.uuid;
  }

  return null;
};

// NOTE: First reachable project inside one specific organization, or null when
// the user has none there (including when the org is absent from the list
// entirely, which is what a non-member looks like).
const getFirstProjectUuidInOrganization = ({
  projects,
  organizationUuid,
}: IGetFirstProjectUuidInOrganizationProps): string | null => {
  const organization = projects.organizationProjects.find(
    (candidate) => candidate.uuid === organizationUuid,
  );

  return organization?.projects[0]?.uuid ?? null;
};

// NOTE: Prefer the last-used project, but only if it's still accessible — guards
// against a stale/deleted project.
//
// NOTE: shared because both clients need exactly this decision and had grown
// their own copy of it. Web restores the last-used uuid from `localStorage`;
// mobile restores it from device storage on a cold start (NSAI-4291), where an
// unchecked stale uuid is worse than a wrong landing page — the API client sends
// whatever storage holds as `X-Project-Uuid`, so every project-scoped request
// fails until the user switches projects by hand. One implementation means the
// two platforms cannot drift on which project a returning user lands in.
export const resolveInitialProjectUuid = ({
  projects,
  lastUsedProjectUuid,
  preferredOrganizationUuid,
}: IResolveInitialProjectUuidProps): string | null => {
  if (
    lastUsedProjectUuid &&
    getIsProjectAccessible({ projects, projectUuid: lastUsedProjectUuid })
  ) {
    return lastUsedProjectUuid;
  }

  // NOTE: NSAI-5021 — a SAML session should LAND in the organization whose IdP
  // it authenticated against, but that is a landing preference, not an access
  // boundary. The project list is no longer filtered down to that single org,
  // so without this step an SSO user with several memberships would be dropped
  // into whichever org the server happens to return first — not the tenant they
  // just signed in through. A preference the user cannot satisfy (no project in
  // that org) falls through to the normal default instead of stranding them.
  if (preferredOrganizationUuid) {
    const preferredProjectUuid = getFirstProjectUuidInOrganization({
      projects,
      organizationUuid: preferredOrganizationUuid,
    });

    if (preferredProjectUuid) {
      return preferredProjectUuid;
    }
  }

  return getDefaultProjectUuid(projects);
};

// NOTE: NSAI-4865 — the project the app should be scoped to RIGHT NOW, given
// what the server just said the user can reach. Unlike
// resolveInitialProjectUuid this also re-checks an already-selected project, so
// access that disappears mid-session (SAML de-provisioning, an admin removing
// the member, a deleted project) moves the user to a project they still have
// instead of leaving the stored pointer aimed at a dead one. That pointer rides
// on every request as the X-Project-Uuid header, and a project the user cannot
// reach used to be answered with 401 — which clients turn into a forced
// sign-out, so a stale pointer read to the user as being locked out of the whole
// app (JwtAuthGuard now answers 403 instead).
// Returns null only when there is genuinely nothing to select.
export const resolveActiveProjectUuid = ({
  projects,
  currentProjectUuid,
  lastUsedProjectUuid,
  preferredOrganizationUuid,
}: IResolveActiveProjectUuidProps): string | null => {
  if (
    currentProjectUuid &&
    getIsProjectAccessible({ projects, projectUuid: currentProjectUuid })
  ) {
    return currentProjectUuid;
  }

  // NOTE: NSAI-5021 — the SAML landing preference only steers the FALLBACK. A
  // project the user can still reach outranks it, so an SSO user who switched
  // organizations by hand is not dragged back to their IdP org on the next
  // project-list refresh; it applies exactly when the previous pointer died and
  // a new one has to be picked.
  return resolveInitialProjectUuid({
    projects,
    lastUsedProjectUuid,
    preferredOrganizationUuid,
  });
};
